import type { FieldSuggestion } from "@kundeninfo/api-client";

/*
 * Alle Anwendertexte der Rollen am Geschäftspartner (Lieferanten Strang A, Aufgabe 6; Hausregel „Texte in
 * Properties“, eine Tabelle je Sprache): Schlüssel englisch, Werte deutsch, Wortlaut aus Spec „Wortwahl“ und C9.
 * „Geschäftspartner“ steht, wo die Firma unabhängig von der Rolle gemeint ist; „Kunde“/„Lieferant“ für die Rolle.
 */

export const PARTNER_TEXT = {
  navLabel: "Geschäftspartner",
  /** Der Rückweg aus der Akte (Entscheid 515) — er nennt das Ziel wie die übrigen Rückwege in App.tsx. */
  backToList: "Zurück zur Liste der Geschäftspartner",
  /** Die leere Fokusspalte ohne offene Akte (Entscheid 515). */
  noneSelected: "Kein Geschäftspartner ausgewählt.",
  /** Der zugängliche Name der Reiterleiste der Akte (Entscheid 515). */
  recordTabs: "Bereiche der Akte",
  /** Der Weg aus „Diese Seite gibt es nicht.“ (Fixrunde 1 zu 515, K3). */
  toList: "Zur Liste der Geschäftspartner",
  /** Die Umleitung eines Nicht-Administrators aus der Verwaltung (Fixrunde 1 zu 515, K3). */
  adminAreaRedirect: "Die Verwaltung ist Administratoren vorbehalten. Hier ist die Liste der Geschäftspartner.",
  /** Der Fenstertitel einer offenen Akte (Spec „Wortwahl“, Aufgabe 22). */
  recordTitle: (firma: string): string => `Akte ${firma}`,
  /** Der Name einer Zeile der Liste (zugänglich): die Firma als Geschäftspartner jeder Rolle (Aufgabe 22). */
  rowName: (teile: readonly string[]): string => [`Geschäftspartner ${teile[0] ?? ""}`, ...teile.slice(1), "anzeigen"].join(", "),
  newPartner: "Neuer Geschäftspartner",
  create: "Geschäftspartner anlegen",
  /** Der Anlegen-Knopf, solange der Dublettenhinweis steht (verboten wird nichts). */
  createAnyway: "Trotzdem neu anlegen",
  /** Der Sprung in die Akte des Partners: am Dublettentreffer und nach „angelegt, Logo gescheitert“. */
  toRecord: "Zur Kundenakte",
  searchLabel: "Geschäftspartner durchsuchen",
  notFound: "Kein Geschäftspartner gefunden.",
  /** Die Rollen-Chips der Liste (C9) — Mehrzahl, weil sie eine Gruppe von Zeilen zeigen. */
  chips: { CUSTOMER: "Kunden", PROSPECT: "Interessenten", SUPPLIER: "Lieferanten" },
  chipsLabel: "Geschäftspartner nach Rolle",
  showFormer: "ehemalige zeigen",
  /** Die Rollen als Kennzeichen (Fläche, kein Rahmen) — Kundenrolle aus `status`, Lieferantenrolle aus `supplierStatus`. */
  badge: {
    CUSTOMER: "Kunde", PROSPECT: "Interessent", DORMANT: "Ruhend", FORMER: "Ehemalig",
    SUPPLIER: "Lieferant", SUPPLIER_FORMER: "ehem. Lieferant",
  },
  /** Die Rollen als Kürzel-Chips der Geschäftspartnerliste (Entscheid 525a); der volle Name steht in `badge` und ist der
   *  zugängliche Name des Chips. Ehemalige tragen den Zusatz „ehem.“. */
  roleShort: {
    CUSTOMER: "K", PROSPECT: "I", DORMANT: "R", FORMER: "ehem. K",
    SUPPLIER: "L", SUPPLIER_FORMER: "ehem. L",
  },
  roleChoice: "Rolle",
  roleCustomer: "Kunde",
  roleProspect: "Interessent",
  roleSupplier: "Lieferant",
  relationAt: (firma: string): string => `Beziehung bei ${firma}`,
  rolesPerCompany: "Rollen je Unternehmen",
  duplicate: (name: string, rollen: string): string => `Geschäftspartner existiert bereits: ${name}, ${rollen}`,
  /** „Lieferant bei ME“, „Kunde und Lieferant bei PA“ — die Rollen EINER Beziehung im Dublettenhinweis. */
  rolesAt: (rollen: readonly string[], kuerzel: string): string => `${rollen.join(" und ")} bei ${kuerzel}`,
  noCurrentRole: "ohne laufende Rolle",
  addCustomerRole: "Rolle Kunde hinzufügen",
  confirmAddCustomerRole: (name: string, kuerzel: string): string => `${name} bei ${kuerzel} als Kunde führen?`,
  addCustomerRoleFailed: "Die Rolle Kunde liess sich nicht hinzufügen.",
  add: "Hinzufügen",
  numbers: "Nummern",
  customerNumber: "Kundennummer",
  /** „Kundennummer K-1042“ — im zugänglichen Namen einer Zeile der Liste (A-K4). */
  customerNumberValue: (nummer: string): string => `Kundennummer ${nummer}`,
  customerNumberHint: "Leer lassen — die Nummer wird beim Speichern vergeben.",
  /** Die Anlege-Maske nennt zusätzlich den Weg für eine schon bestehende Nummer. */
  customerNumberHintCreate: "Leer lassen — die Nummer wird beim Speichern vergeben. Eine bestehende Nummer "
    + "(z. B. aus dem Portal) trägst du hier ein.",
  customerSince: "Kunde seit",
  /** Der Dublettenhinweis der Anlege-Maske (819: nur beim Anlegen). */
  duplicatesRegion: "Mögliche Dubletten",
  duplicatesTitle: "Gibt es diese Firma schon?",
  duplicatesText: "Ähnlich benannte Firmen sind bereits erfasst. Gehört eine davon zu einem anderen Unternehmen, "
    + "wechsle dorthin und füge in der Kundenakte unter Stammdaten dein Unternehmen hinzu — so entsteht keine zweite "
    + "Firmenzeile.",
  switchTo: (kuerzel: string): string => `Zu ${kuerzel} wechseln`,
  /** Angelegt, nur das Logo scheiterte: kein zweites Anlegen, sondern der Weg in die Akte. */
  createdWithoutLogo: (grund: string): string =>
    `Der Kunde wurde angelegt, das Logo konnte aber nicht übernommen werden: ${grund}`,
  editTitle: (firma: string): string => `Stammdaten von ${firma} bearbeiten`,
  /** Die Hinweiszeile „fremder Stand“ der Bearbeitung (409). */
  conflictBy: (wer: string, wann: string): string => `${wer} hat diesen Kunden inzwischen geändert (${wann}).`,
  conflictUnknown: "Dieser Kunde wurde inzwischen geändert.",
  conflictCompare: "Deine Eingaben sind nicht verloren — vergleiche beide Stände und entscheide.",
  conflictMine: (wert: string): string => `Deine Eingabe: ${wert}`,
  conflictTheirs: (wert: string): string => `Aktueller Stand: ${wert}`,
  keepMine: "Meine Eingaben behalten",
  /** Die Kundenrolle kommt zurück und die Beziehung hat schon eine Nummer: Sie wird wieder genutzt (C7). */
  customerNumberReused: (nummer: string): string => `Kundennummer ${nummer} wird wieder genutzt`,
  creditor: "Kreditornummer",
  creditorNext: "Nächste freie",
  creditorNone: "Kein Kreditor",
  creditorValue: (nummer: string): string => `Kreditor ${nummer}`,
  creditorEnter: "Kreditornummer eintragen",
  creditorDialog: (firma: string): string => `Kreditornummer für ${firma}`,
  // Die drei Nummernfragen (Kreditor unter Beziehungen, Debitor unter Beziehungen und in der DATEV-Kopplung; Entscheid
  // 686) in EINER Form: der Titel nennt den KUNDEN, der Satz den Kreis des Unternehmens und — falls schon eine Nummer
  // steht — dass sie ersetzt wird (`setzeDebitorennummer`/`setzeKreditornummer` schreiben „NAECHSTE“ ohne Rücksicht auf
  // eine vorhandene Nummer). Gefahr-Gestalt nur dann (Rot-Regel: Kopf von `useBestaetigung`).
  confirmCreditorNext: (kunde: string): string => `Nächste freie Kreditornummer für ${kunde} vergeben?`,
  creditorNextText: (unternehmen: string, bisher: string | null): string =>
    `Die Nummer kommt als nächste freie aus dem Kreditorenkreis von ${unternehmen}.`
    + (bisher === null ? "" : ` Die bisherige Kreditornummer ${bisher} wird dabei ersetzt.`),
  creditorAssign: "Vergeben",
  confirmDebtorNext: (kunde: string): string => `Nächste freie Debitorennummer für ${kunde} vergeben?`,
  debtorNextText: (unternehmen: string | null, bisher: string | null): string =>
    `Die Nummer kommt als nächste freie aus dem Debitorenkreis ${unternehmen === null ? "dieses Unternehmens" : `von ${unternehmen}`}.`
    + (bisher === null ? "" : ` Die bisherige Debitorennummer ${bisher} wird dabei ersetzt.`),
  debtorAssign: "Vergeben",
  creditorFailed: "Die Kreditornummer liess sich nicht vergeben.",
  changeRoles: "Rollen ändern",
  changeRolesAt: (kuerzel: string): string => `Rollen bei ${kuerzel} ändern`,
  rolesDialog: (firma: string, kuerzel: string): string => `Rollen von ${firma} bei ${kuerzel}`,
  customerRole: "Kundenrolle",
  supplierRole: "Lieferantenrolle",
  none: "keine",
  save: "Speichern",
  cancel: "Abbrechen",
  atLeastOne: "Mindestens eine Rolle bleibt gewählt.",
  /** Ein leerer Wert als Platzhalter („–“), wo ein Feld stehen bleibt (Beziehungsüberschrift der Anlegemaske). */
  empty: "–",
  /** Der Satz über den Beziehungen in „Rollen je Unternehmen“ (`relationsSentence`): je Unternehmen der Name und seine
   *  Rollen mit den Wörtern der Kennzeichen (`badge`) — nie ein Schlüssel des Vertrags (Handbuch-Übergabe 02.11.2026). */
  noRelation: "Noch keinem Unternehmen zugeordnet.",
  relationsLine: (eintraege: readonly string[]): string =>
    `Zugeordnet bei ${eintraege.length} Unternehmen: ${eintraege.join(", ")}`,
  relationEntry: (firma: string, rollen: readonly string[]): string =>
    (rollen.length === 0 ? firma : `${firma} (${rollen.join(" und ")})`),
} as const;

/**
 * Der eine Dialog für „Geschäftspartner anlegen“ und „Stammdaten bearbeiten“ (Entscheide 814–819): Beschriftungen der
 * Stammdatenfelder, die Hilfetexte an USt-IdNr. und Webseite (818), Rechnungsformate, Vorschläge und die Fragen des
 * Verlassen-Wächters — einmal für beide Modi.
 */
export const PARTNER_FORM_TEXT = {
  field: {
    companyName: "Firmenname", street: "Straße", houseNumber: "Hausnummer", postalCode: "PLZ", city: "Ort",
    phone: "Telefon", email: "E-Mail", website: "Webseite", vatId: "USt-IdNr.", industry: "Branche",
    country: "Land", leitwegId: "Leitweg-ID", invoiceFormat: "Rechnungsformat",
  },
  /** 818: kurz, direkt am Feld — statt des Erklärtexts über der Anlege-Maske. */
  vatIdHelp: "Mit der USt-IdNr. prüft das System VIES und schlägt Stammdaten vor.",
  websiteHelp: "Mit der Webseite schlägt das System Stammdaten aus dem Impressum vor; Logos holt der Knopf daneben.",
  /** Die Rechnungsformate (Migration 025); die Schlüssel kommen aus dem Vertrag. */
  invoiceFormats: { PDF_A3: "PDF", ZUGFERD_EN16931: "ZUGFeRD (PDF mit XML)", XRECHNUNG_30: "XRechnung (XML)" },
  /** 816: in beiden Modi, solange XRechnung gewählt und die Leitweg-ID leer ist. */
  xrechnungNeedsLeitweg: "XRechnung braucht eine Leitweg-ID — bitte im Feld „Leitweg-ID“ erfassen.",
  /** Der VIES-Befund am Status-Chip neben der USt-IdNr. — „Übernehmen“/„übernommen“ der Funde bringt `me-fund` mit. */
  viesValid: "laut VIES gültig",
  viesInvalid: "laut VIES ungültig",
  leaveTitle: "Seite verlassen?",
  leaveText: "Es gibt ungespeicherte Änderungen. Sie gehen verloren.",
  leaveConfirm: "Verlassen",
} as const;

/** Die Quelle eines Anreicherungs-Vorschlags, für den Bearbeiter benannt — am Feldvorschlag und an der Logo-Kachel. */
export const SOURCE_LABEL: Record<FieldSuggestion["source"], string> = {
  VIES: "VIES", IMPRESSUM: "Impressum", WEBSITE: "Webseite", CREDITREFORM: "Creditreform",
};

/**
 * Die Logo-Wahl aus der Anreicherung (Aufgabe 7) — beim Anlegen und seit Entscheid 673 (02.11.2026) auch in den
 * Stammdaten eines bestehenden Geschäftspartners (Knopf neben dem Feld „Webseite“).
 */
export const LOGO_TEXT = {
  tiles: "Logo-Vorschläge",
  helpCreate: "Zur Übernahme ein Bild wählen — es wird erst nach dem Anlegen des Kunden geladen.",
  helpEdit: "Zur Übernahme ein Bild wählen — es wird erst beim Speichern übernommen.",
  helpReplace: "Zur Übernahme ein Bild wählen — es ersetzt das bisherige Logo erst beim Speichern.",
  /** Die Keins-Kachel ohne vorhandenes Logo: Name und Bildtext. */
  none: "Kein Logo",
  noneImage: "ohne Logo",
  /** Die Kachel des vorhandenen Logos (sie zeigt es als Bild): Sie ändert nichts — einen Weg „Logo entfernen“ gibt es
   *  hier nicht. Kurz, damit die Unterschrift der Kachel nicht abgeschnitten wird. */
  keep: "Logo behalten",
  candidate: (n: number): string => `Vorschlag ${n}`,
  /** Eine übernommene Wahl, die ein neuer Abruf nicht mehr vorschlägt. */
  taken: "Übernommen",
  dialogTitle: "Logo übernehmen",
  take: "Übernehmen",
  /** Markenrechts-Hinweis — Wortlaut EXAKT aus dem Fundament (Abschnitt „Logos sind geschützt“), nicht kürzen. */
  trademark: "Ein Firmenlogo ist Marke und urheberrechtlich geschützt. Die Verwendung zur Wiedererkennung in der "
    + "internen Kundenakte ist vertretbar; eine Verwendung nach außen wäre es nicht.",
  fetch: "Logo-Vorschläge aus der Webseite holen",
  fetching: "Logo-Vorschläge werden geholt …",
  needsDomain: "Für Logo-Vorschläge eine Webseite mit Domain eintragen.",
  /** Leer kommt der Abruf auch bei gesperrter, langsamer oder unerreichbarer Seite — der Satz behauptet nicht mehr. */
  noCandidates: "Auf der Webseite wurde kein Logo gefunden.",
  failed: "Die Logo-Vorschläge konnten gerade nicht geholt werden.",
  /** Der Grund, wenn ein gescheitertes Hochladen keinen eigenen nennt. */
  unknownError: "unbekannter Fehler",
  savedWithoutLogo: (grund: string): string =>
    `Die Stammdaten wurden gespeichert, das Logo konnte aber nicht übernommen werden: ${grund}`,
} as const;
